/**
 * Reports Page
 * Cost analysis and operational reports dashboard with tabs
 */

import { useState } from 'react';
import clsx from 'clsx';
import { BarChart3, AlertCircle, Users, Activity, DollarSign, MousePointer, TrendingUp } from 'lucide-react';
import { useI18n } from '@/i18n';
import { Loading, TeamExclusionCheckbox } from '@/components/common';
import { useReportsDashboard } from '@/features/reports/hooks/useReports';
import { getReportPageItem, type ReportPageId } from '@/features/reports/reportNavigation';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';
import {
  StatsCard,
  CostPieChart,
  DailyTrendChart,
  SessionsTrendChart,
  UserCostTable,
  CostDistributionChart,
  TopUsersChart,
  PricingScenarios,
  TrialRiskAnalysis,
  DateRangeSelector,
  // Behavior analytics components
  TopEventsChart,
  DailyEventsChart,
  FeatureUsageChart,
  FeatureUsageTable,
  ScreenViewsChart,
  UserJourneyFunnel,
  RetentionCards,
  BehaviorSummaryCards,
} from '@/features/reports/components';
import { useAnalyticsDashboard, useAnalyticsHealth } from '@/features/reports/hooks/useAnalytics';
import { RevenueAnalysis } from '@/features/reports/components/RevenueAnalysis';

const reportPageIconById: Record<ReportPageId, typeof BarChart3> = {
  overview: BarChart3,
  userCost: Users,
  pricing: DollarSign,
  trialRisk: Activity,
  behavior: MousePointer,
  revenue: TrendingUp,
};

interface ReportsPageProps {
  pageId?: ReportPageId;
}

export function ReportsPage({ pageId = 'overview' }: ReportsPageProps) {
  const { t } = useI18n();
  const reportPage = getReportPageItem(pageId);
  const HeaderIcon = reportPageIconById[reportPage.id];
  const showsTeamExclusion = (
    reportPage.id === 'overview' ||
    reportPage.id === 'userCost' ||
    reportPage.id === 'pricing' ||
    reportPage.id === 'trialRisk' ||
    reportPage.id === 'behavior' ||
    reportPage.id === 'revenue'
  );

  // Date range state — default to last 1 year to cover all historical revenue
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 365);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [excludeTeam, setExcludeTeam] = useState(DEFAULT_EXCLUDE_TEAM);

  const {
    data: dashboard,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useReportsDashboard({
    startDate,
    endDate,
    excludeTeam,
    enabled: true,
  });

  const formatCurrency = (value: number) => `¥${value.toFixed(2)}`;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <HeaderIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t(`navigation.${reportPage.navigationKey}`)}</h1>
            <p className="text-sm text-gray-500 mt-1">{t('reportsPage.pageDescription')}</p>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-gray-200">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onRefresh={() => refetch()}
            isRefreshing={isFetching}
            className="flex-1"
          />
          {showsTeamExclusion && (
            <TeamExclusionCheckbox
              label={t('reportsPage.filters.excludeTeam')}
              checked={excludeTeam}
              onCheckedChange={setExcludeTeam}
            />
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loading message={t('reportsPage.loading')} />
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-red-900 mb-1">{t('reportsPage.loadFailed')}</h3>
            <p className="text-sm text-red-700">
              {error instanceof Error ? error.message : t('reportsPage.fetchFailed')}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 underline"
            >
              {t('reportsPage.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Content */}
      {dashboard && (
        <div className="space-y-6">
          {/* Last Update */}
          <div className="text-sm text-gray-500">
            {t('reportsPage.dataUpdatedAt')}: {new Date(dashboard.timestamp).toLocaleString()}
            {' · '}
            {t('reportsPage.dateRange')}: {dashboard.date_range.start} ~ {dashboard.date_range.end}
          </div>

          {/* Tab Content */}
          {reportPage.id === 'overview' && (
            <OverviewTab dashboard={dashboard} formatCurrency={formatCurrency} t={t} />
          )}
          {reportPage.id === 'userCost' && (
            <UsersTab dashboard={dashboard} formatCurrency={formatCurrency} t={t} />
          )}
          {reportPage.id === 'pricing' && (
            <PricingTab dashboard={dashboard} t={t} />
          )}
          {reportPage.id === 'trialRisk' && (
            <TrialTab dashboard={dashboard} t={t} />
          )}
          {reportPage.id === 'behavior' && (
            <BehaviorTab startDate={startDate} endDate={endDate} excludeTeam={excludeTeam} t={t} />
          )}
          {reportPage.id === 'revenue' && (
            <RevenueAnalysis startDate={startDate} endDate={endDate} excludeTeam={excludeTeam} />
          )}
        </div>
      )}
    </div>
  );
}

export function ReportsOverviewPage() {
  return <ReportsPage pageId="overview" />;
}

export function ReportsUserCostPage() {
  return <ReportsPage pageId="userCost" />;
}

export function ReportsPricingPage() {
  return <ReportsPage pageId="pricing" />;
}

export function ReportsTrialRiskPage() {
  return <ReportsPage pageId="trialRisk" />;
}

export function ReportsBehaviorPage() {
  return <ReportsPage pageId="behavior" />;
}

export function ReportsRevenuePage() {
  return <ReportsPage pageId="revenue" />;
}

// ============================================================
// Tab Components
// ============================================================

interface TabProps {
  dashboard: NonNullable<ReturnType<typeof useReportsDashboard>['data']>;
  formatCurrency?: (value: number) => string;
  t: (key: string) => string;
}

function OverviewTab({ dashboard, formatCurrency = (v) => `¥${v.toFixed(2)}`, t }: TabProps) {
  const { summary, daily_stats, soulmate_stats, cost_tiers, config } = dashboard;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatsCard
          label={t('reportsPage.overview.totalUsers')}
          value={summary.total_users.toString()}
          subtitle={t('reportsPage.overview.activeUsers')}
        />
        <StatsCard
          label={t('reportsPage.overview.totalSessions')}
          value={summary.total_sessions.toString()}
          subtitle={t('reportsPage.overview.chatCount')}
        />
        <StatsCard
          label={t('reportsPage.overview.chatCost')}
          value={formatCurrency(summary.total_chat_cost_cny)}
          variant="danger"
        />
        <StatsCard
          label={t('reportsPage.overview.dailyServiceCost')}
          value={formatCurrency(summary.total_daily_service_cost_cny)}
          variant="warning"
        />
        <StatsCard
          label={t('reportsPage.overview.totalCost')}
          value={formatCurrency(summary.total_cost_cny)}
          variant="danger"
        />
        <StatsCard
          label={t('reportsPage.overview.avgUserCost')}
          value={formatCurrency(summary.avg_cost_per_user_cny)}
          subtitle={`${t('reportsPage.overview.median')}: ${formatCurrency(summary.median_cost_cny)}`}
        />
      </div>

      {/* Profitability Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          label={t('reportsPage.overview.memberPrice')}
          value={formatCurrency(config.member_price)}
          subtitle={t('reportsPage.overview.perMonth')}
        />
        <StatsCard
          label={t('reportsPage.overview.profitableUsers')}
          value={`${summary.profitable_users}${t('reportsPage.overview.people')}`}
          subtitle={`${t('reportsPage.overview.ratio')} ${(summary.profitable_rate * 100).toFixed(1)}%`}
          variant={summary.profitable_rate >= 0.5 ? 'profit' : 'warning'}
        />
        <StatsCard
          label={t('reportsPage.overview.p75Cost')}
          value={formatCurrency(summary.cost_percentiles['p75'] || 0)}
          subtitle={t('reportsPage.overview.p75Subtitle')}
        />
        <StatsCard
          label={t('reportsPage.overview.p90Cost')}
          value={formatCurrency(summary.cost_percentiles['p90'] || 0)}
          subtitle={t('reportsPage.overview.p90Subtitle')}
          variant={(summary.cost_percentiles['p90'] || 0) > config.member_price ? 'danger' : 'profit'}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <CostPieChart summary={summary} />
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <CostDistributionChart costTiers={cost_tiers} />
        </div>
      </div>

      {/* Daily Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <DailyTrendChart dailyStats={daily_stats} />
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <SessionsTrendChart dailyStats={daily_stats} />
        </div>
      </div>

      {/* Soulmate Stats */}
      {soulmate_stats && soulmate_stats.length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.overview.soulmateStats')}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {soulmate_stats.map((stat) => (
              <div key={stat.type} className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">
                  {stat.type === 'lifetime' ? t('reportsPage.overview.lifetimeContract') : t('reportsPage.overview.freeMatch')}
                </div>
                <div className="text-xl font-bold text-gray-900">{stat.count}{t('reportsPage.overview.times')}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {t('reportsPage.overview.cost')}: {formatCurrency(stat.cost_cny)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab({ dashboard, formatCurrency = (v) => `¥${v.toFixed(2)}`, t }: TabProps) {
  const { users, cost_tiers, config } = dashboard;

  return (
    <div className="space-y-6">
      {/* Top Users Chart */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <TopUsersChart users={users} memberPrice={config.member_price} limit={15} />
      </div>

      {/* Cost Tier Distribution */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.users.costTierAnalysis')}</h4>
        <UserCostTable costTiers={cost_tiers} config={config} />
      </div>

      {/* User Details Table */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.users.userDetails')}</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.user')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.phone')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.sessionCount')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.lifecycle')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.chatCost')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.serviceCost')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.totalCost')}</th>
                <th className="px-3 py-2 text-left text-gray-500">{t('reportsPage.users.profitLoss')}</th>
              </tr>
            </thead>
            <tbody>
              {users.slice(0, 50).map((user) => {
                const profit = config.member_price - user.total_cost_cny;
                return (
                  <tr
                    key={user.user_id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 text-gray-900">
                      {user.name || `User_${user.user_id.slice(0, 8)}`}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {user.phone || '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-900">{user.session_count}</td>
                    <td className="px-3 py-2 text-gray-600">{user.lifecycle_days}{t('reportsPage.users.days')}</td>
                    <td className="px-3 py-2 text-red-600">
                      {formatCurrency(user.chat_cost_cny)}
                    </td>
                    <td className="px-3 py-2 text-amber-600">
                      {formatCurrency(user.daily_service_cost)}
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {formatCurrency(user.total_cost_cny)}
                    </td>
                    <td
                      className={clsx(
                        'px-3 py-2 font-medium',
                        profit >= 0 ? 'text-green-600' : 'text-red-600'
                      )}
                    >
                      {formatCurrency(profit)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PricingTab({ dashboard }: TabProps) {
  const { pricing_scenarios } = dashboard;

  return (
    <div className="space-y-6">
      <PricingScenarios scenarios={pricing_scenarios} dashboard={dashboard} />
    </div>
  );
}

function TrialTab({ dashboard, t }: TabProps) {
  const { trial_analysis } = dashboard;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.trial.riskAnalysis')}</h4>
        <TrialRiskAnalysis data={trial_analysis} />
      </div>
    </div>
  );
}

interface BehaviorTabProps {
  startDate: string;
  endDate: string;
  excludeTeam: boolean;
  t: (key: string) => string;
}

function BehaviorTab({ startDate, endDate, excludeTeam, t }: BehaviorTabProps) {
  const {
    data: analytics,
    isLoading,
    isError,
    error,
  } = useAnalyticsDashboard({
    startDate,
    endDate,
    excludeTeam,
    enabled: true,
  });

  const { data: health } = useAnalyticsHealth();

  // Check if PostHog is configured
  if (health?.status === 'not_configured') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('reportsPage.behavior.posthogNotConfigured')}</h3>
        <p className="text-sm text-amber-700 mb-4">
          {t('reportsPage.behavior.posthogConfigHint')}
        </p>
        <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
          <li><code className="bg-amber-100 px-1 rounded">POSTHOG_PERSONAL_API_KEY</code> - PostHog Personal API Key</li>
          <li><code className="bg-amber-100 px-1 rounded">POSTHOG_HOST</code> - {t('reportsPage.behavior.posthogHostHint')}</li>
        </ul>
        <p className="text-sm text-amber-700 mt-4">
          {t('reportsPage.behavior.mobileConfigHint')}
        </p>
        <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
          <li><code className="bg-amber-100 px-1 rounded">EXPO_PUBLIC_POSTHOG_API_KEY</code></li>
          <li><code className="bg-amber-100 px-1 rounded">EXPO_PUBLIC_POSTHOG_HOST</code></li>
        </ul>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading message={t('reportsPage.behavior.loadingBehavior')} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-1">{t('reportsPage.loadFailed')}</h3>
        <p className="text-sm text-red-700">
          {error instanceof Error ? error.message : t('reportsPage.behavior.fetchFailed')}
        </p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-gray-500">{t('reportsPage.behavior.noData')}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <BehaviorSummaryCards summary={analytics.summary} />

      {/* Retention Cards */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.behavior.userRetention')}</h4>
        <RetentionCards retention={analytics.retention} />
      </div>

      {/* Daily Trends */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <DailyEventsChart dailyStats={analytics.summary.daily_stats} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <TopEventsChart events={analytics.summary.top_events} />
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <FeatureUsageChart features={analytics.feature_usage} />
        </div>
      </div>

      {/* Screen Views */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <ScreenViewsChart screens={analytics.screen_views} />
      </div>

      {/* User Journey Funnel */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <UserJourneyFunnel steps={analytics.user_journey} />
      </div>

      {/* Feature Usage Table */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <FeatureUsageTable features={analytics.feature_usage} />
      </div>
    </div>
  );
}
